<template>
  <div v-if="agentSetup.agentMode === 'Flow' && agentSetup.currentPage === 4" class="bg-base-100 border border-base-300 rounded-box p-6 shadow-sm" style="min-height: 70vh;">
    <div class="flex justify-between items-center mb-4" id="tabTitle">
      <h1 class="text-2xl" :style="agentSetup.isRtl === true ? { direction: 'rtl', textAlign: 'right' } : { direction: 'ltr', textAlign: 'left' }">{{ agentSetup.t('aiChatAgentSetup.agentFlow.title', 'Agent Flow') }}</h1>
    </div>
    <div class="flex">
      <div class="flex-grow" style="width: 100%;">
        <LLMFlow :ref="setLlmFlowRef" @builder-closed="handleBuilderClosed" />
      </div>
      <div class="flex-shrink-0 border-l pl-6 flex flex-col" style="width: 35%;display: none;">
        <div class="custom-tabs-container flex mb-2 border-b border-base-300">
          <button class="custom-tab-button flex-1 px-4 py-2 text-sm font-medium text-center transition-colors duration-150 ease-in-out" :class="{ 'active-tab': agentSetup.activeFlowPane === 'Preview', 'inactive-tab': agentSetup.activeFlowPane !== 'Preview' }" @click="agentSetup.activeFlowPane = 'Preview'">
            {{ agentSetup.t('aiChatAgentSetup.agentFlow.previewTab', 'Preview') }}
          </button>
          <button class="custom-tab-button flex-1 px-4 py-2 text-sm font-medium text-center transition-colors duration-150 ease-in-out" :class="{ 'active-tab': agentSetup.activeFlowPane === 'Flow AI Agent', 'inactive-tab': agentSetup.activeFlowPane !== 'Flow AI Agent' }" @click="agentSetup.activeFlowPane = 'Flow AI Agent'">
            {{ agentSetup.t('aiChatAgentSetup.agentFlow.flowAIAgentTab', 'Flow AI Agent') }}
          </button>
        </div>
        <div class="custom-tab-content flex-grow bg-base-100 rounded-b-box overflow-hidden relative">
          <div class="tab-pane h-full w-full absolute inset-0 p-1 transition-opacity duration-200 ease-in-out" :class="{ 'opacity-100': agentSetup.activeFlowPane === 'Preview', 'opacity-0 pointer-events-none': agentSetup.activeFlowPane !== 'Preview' }">
            <div class="card bg-base-200 p-4 rounded-md">
              <FlowPreviewChat v-if="agentSetup.isEditMode === true && agentSetup.route.query.edit !== undefined" :agent-id="agentSetup.route.query.edit" :key="agentSetup.flowPreviewKey" />
            </div>
          </div>
          <div class="tab-pane h-full w-full absolute inset-0 p-1 transition-opacity duration-200 ease-in-out" :class="{ 'opacity-100': agentSetup.activeFlowPane === 'Flow AI Agent', 'opacity-0 pointer-events-none': agentSetup.activeFlowPane !== 'Flow AI Agent' }">
            <FlowBuilderAgent v-if="agentSetup.isEditMode === true" :agent-id="agentSetup.route.query.edit" :llm-flow-ref="agentSetup.flowRefs.llmFlowRef" @flow-updated="agentSetup.handleFlowUpdateFromAgent" class="h-full w-full" />
            <div v-else class="p-4 text-center text-gray-500">
              <span>Save the agent first to enable the Flow AI Agent.</span>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="flex justify-between mt-4" style="position: relative;">
      <button @click="agentSetup.goNextStep(4, -1)" class="btn">
        <span v-if="agentSetup.isNavigating === 3" class="loading loading-spinner loading-sm mr-2"></span>
        {{ agentSetup.t('aiChatAgentSetup.buttons.previousStep', 'Previous Step') }}
      </button>
      <div class="flex space-x-2">
        <button v-if="agentSetup.isEditMode === true && agentSetup.uploadedFiles.length === 0" @click="agentSetup.saveAgentWithoutRedirect" class="btn btn-neutral">
          <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === false" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.save', 'Save') }}
        </button>
        <button v-if="agentSetup.isEditMode === true" @click="agentSetup.createAgent(true)" class="btn">
          <span v-if="agentSetup.isSaving === true && agentSetup.isSavingAndRedirecting === true" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.saveAndClose', 'Save & Close') }}
        </button>
        <button @click="agentSetup.goNextStep(4, 1)" class="btn">
          <span v-if="agentSetup.isNavigating === 5" class="loading loading-spinner loading-sm mr-2"></span>
          {{ agentSetup.t('aiChatAgentSetup.buttons.nextStep', 'Next Step') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import FlowBuilderAgent from '@/components/FlowBuilderAgent.vue';
import FlowPreviewChat from '@/components/FlowPreviewChat.vue';
import LLMFlow from '@/components/LLMFlow.vue';
import { useAgentSetupContext } from '@/components/AgentSetupContext.js';
import { nextTick, watch } from 'vue';

const agentSetup = useAgentSetupContext();

function setLlmFlowRef(value) {
  agentSetup.setLlmFlowRef(value);
}

function openFlowBuilderPanel() {
  if (agentSetup.openLlmFlowBuilder !== undefined && agentSetup.openLlmFlowBuilder !== null && typeof agentSetup.openLlmFlowBuilder === 'function') {
    agentSetup.openLlmFlowBuilder();
  }
}

function handleBuilderClosed() {
  if (agentSetup.currentPage === 4) {
    agentSetup.goToPage(1);
  }
}

watch(() => [agentSetup.agentMode, agentSetup.currentPage], () => {
  if (agentSetup.agentMode === 'Flow' && agentSetup.currentPage === 4) {
    openFlowBuilderPanel();
    nextTick(() => {
      openFlowBuilderPanel();
      nextTick(() => {
        openFlowBuilderPanel();
      });
    });
  }
}, { immediate: true, flush: 'post' });
</script>
